import { CalculatorOutlined } from '@ant-design/icons';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { StyledHtmlText } from '@framework/engine/inputs/rich-text/style/StyledHtmlText.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { Row, Typography } from 'antd';
import DOMPurify from 'dompurify';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmissionBurdenLogic } from '../../../burden/SubmissionBurdenLogic.ts';

export interface ISimpleSubmissionSummaryProps {
  text?: string | null;
  submission?: RichiestaDTO;
  modules?: RichiestaModuloDTO[];
  hasInlineDownload?: boolean;
  PaymentStateTag: React.ComponentType<{ state: any }>;
  ModuleSummary: React.ComponentType<{
    module: RichiestaModuloDTO;
    submission: RichiestaDTO | null | undefined;
    hasInlineDownload?: boolean;
  }>;
}

/**
 * Riepilogo semplice della richiesta con lista moduli inline.
 * Utilizzato quando modalitaRiepilogo non è 'ESTESO'.
 */
export function SimpleSubmissionSummary(props: ISimpleSubmissionSummaryProps) {
  const {
    text,
    submission,
    modules,
    hasInlineDownload,
    PaymentStateTag,
    ModuleSummary,
  } = props;
  const { t } = useTranslation();
  const { statoOneri } = useMemo(() => submission ?? {}, [submission]);
  const showPaymentStatus = SubmissionBurdenLogic.hasBurdens(statoOneri);

  return (
    <>
      {text && (
        <Typography.Paragraph>
          <StyledHtmlText
            // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitize is safe
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text),
            }}
          />
        </Typography.Paragraph>
      )}
      <Row>
        {modules &&
          modules.length > 0 &&
          modules.map(module => (
            <ModuleSummary
              key={module.id}
              module={module}
              submission={submission}
              hasInlineDownload={hasInlineDownload}
            />
          ))}
        {showPaymentStatus && (
          <div style={{ width: '100%', marginBottom: 12 }}>
            <FlexRow
              style={{ width: '100%' }}
              gutter={12}
              left={
                <Typography.Text strong style={{ fontSize: '1.1rem' }}>
                  <CalculatorOutlined aria-hidden />{' '}
                  {t('submission-summary.stato-oneri', 'Stato Oneri')}
                </Typography.Text>
              }
              right={<PaymentStateTag state={statoOneri} />}
            />
          </div>
        )}
      </Row>
    </>
  );
}
